import { of, EMPTY, fromEvent, merge } from 'rxjs';
import { switchMap, catchError, tap, defaultIfEmpty } from 'rxjs/operators';
import { fetchJson, putJson, deleteEntity } from './http.js';
import { renderTemplate, replaceViewContainer, showError } from './ui.js';
import { populateSubjectSelect } from './subjects.js';
import { populateSchoolGradeSelect } from './school-grades.js';
import { createTopicsWidget } from './topics-widget.js';

/**
 * Renders the edit-card view for the card with the given id. Pre-fills the
 * front and back textareas from GET /api/cards/:id. Wires "Speichern"
 * (including Ctrl+Enter), "Löschen" (with confirmation), and "Abbrechen"
 * (history.back). Returns a merged Observable of all active streams.
 */
export function createEditView(id) {
    return of(undefined).pipe(
        switchMap(() => {
            let el = renderTemplate('edit-view');
            replaceViewContainer(el);

            let frontInput = el.querySelector('.card-front-input');
            let backInput = el.querySelector('.card-back-input');
            let backAudioInput = el.querySelector('.card-back-audio-input');
            let grundschuleSelect = el.querySelector('.grundschule-select');
            let gymnasiumSelect = el.querySelector('.gymnasium-select');
            let subjectSelect = el.querySelector('.subject-select');
            let audioInput = el.querySelector('.audio-playback-input');
            let statusEl = el.querySelector('.status');
            let saveBtn = el.querySelector('.save-btn');
            let deleteBtn = el.querySelector('.delete-btn');
            let cancelBtn = el.querySelector('.cancel-btn');

            populateSchoolGradeSelect(grundschuleSelect, 'grundschule');
            populateSchoolGradeSelect(gymnasiumSelect, 'gymnasium');

            let save$ = fromEvent(saveBtn, 'click');
            let ctrlEnter$ = fromEvent(el, 'keydown').pipe(
                switchMap((e) =>
                    e.ctrlKey && e.key === 'Enter' ? of(e) : EMPTY
                )
            );

            let delete$ = fromEvent(deleteBtn, 'click').pipe(
                switchMap(() => {
                    if (!window.confirm('Karte wirklich löschen?')) {
                        return EMPTY;
                    }
                    return deleteEntity(`/api/cards/${id}`).pipe(
                        defaultIfEmpty(null),
                        tap(() => history.back()),
                        catchError((err) => {
                            showError(err);
                            return EMPTY;
                        })
                    );
                })
            );

            let cancel$ = fromEvent(cancelBtn, 'click').pipe(
                tap(() => history.back())
            );

            let subjects$ = populateSubjectSelect(subjectSelect);

            let load$ = fetchJson(`/api/cards/${id}`).pipe(
                switchMap((card) => {
                    frontInput.value = card.front;
                    backInput.value = card.back;
                    backAudioInput.value =
                        card.back_audio_text != null
                            ? card.back_audio_text
                            : '';
                    grundschuleSelect.value =
                        card.grundschule_class != null
                            ? String(card.grundschule_class)
                            : '';
                    gymnasiumSelect.value =
                        card.gymnasium_class != null
                            ? String(card.gymnasium_class)
                            : '';
                    subjectSelect.value =
                        card.subject_id != null ? String(card.subject_id) : '';
                    audioInput.checked = !card.audio_disabled;

                    let topicsWidget = createTopicsWidget(
                        el.querySelector('.topics-widget-container'),
                        card.topic_ids || []
                    );

                    function submitSave() {
                        let front = frontInput.value.trim();
                        let back = backInput.value.trim();
                        let backAudioText = backAudioInput.value.trim();
                        if (!front || !back) {
                            statusEl.textContent =
                                'Bitte Vorderseite und Rückseite ausfüllen.';
                            return EMPTY;
                        }
                        let grundschuleClass = grundschuleSelect.value
                            ? parseInt(grundschuleSelect.value, 10)
                            : null;
                        let gymnasiumClass = gymnasiumSelect.value
                            ? parseInt(gymnasiumSelect.value, 10)
                            : null;
                        let subjectId = subjectSelect.value
                            ? parseInt(subjectSelect.value, 10)
                            : null;
                        let topics = topicsWidget
                            .getSelectedTopics()
                            .map((t) => t.id);
                        statusEl.textContent = 'Wird gespeichert…';
                        return putJson(`/api/cards/${id}`, {
                            front,
                            back,
                            grundschuleClass,
                            gymnasiumClass,
                            subjectId,
                            topics,
                            audioDisabled: !audioInput.checked,
                            backAudioText: backAudioText || null,
                        }).pipe(
                            tap(() => {
                                statusEl.textContent = 'Gespeichert.';
                            }),
                            catchError((err) => {
                                showError(err);
                                return EMPTY;
                            })
                        );
                    }

                    return merge(
                        save$.pipe(switchMap(() => submitSave())),
                        ctrlEnter$.pipe(switchMap(() => submitSave())),
                        topicsWidget.widget$
                    );
                }),
                catchError((err) => {
                    showError(err);
                    return EMPTY;
                })
            );

            return merge(load$, delete$, cancel$, subjects$);
        })
    );
}
